import { useState, type FormEvent } from 'react';
import { GlobalButton } from './GlobalButton';
import { GlobalDatepicker } from './GlobalDatepicker';
import { GlobalDropdown } from './GlobalDropdown';
import { GlobalInput } from './GlobalInput';
import { GlobalTextarea } from './GlobalTextarea';
import { Favicon } from '../utils/favicon';
import { REAL_ESTATE_TYPE_OPTIONS } from '../lib/realEstate';
import type { RealEstateProperty, RealEstatePropertyType } from '../types';

export type RealEstatePropertyFormValues = {
  name: string;
  propertyType: RealEstatePropertyType;
  address?: string;
  websiteUrl?: string;
  currentValue: number;
  purchasePrice?: number;
  purchaseDate?: string;
  mortgageBalance?: number;
  monthlyRent?: number;
  appreciationRate?: number;
  notes?: string;
};

type Props = {
  saving?: boolean;
  error?: string | null;
  /** When provided, prefills the form for editing an existing property. */
  property?: Pick<
    RealEstateProperty,
    | 'name'
    | 'propertyType'
    | 'address'
    | 'websiteUrl'
    | 'currentValue'
    | 'purchasePrice'
    | 'purchaseDate'
    | 'mortgageBalance'
    | 'monthlyRent'
    | 'appreciationRate'
    | 'notes'
  >;
  submitLabel?: string;
  onSubmit: (values: RealEstatePropertyFormValues) => void;
  onCancel: () => void;
};

function numberToInput(value: number | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : '';
}

function parseOptionalNumber(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function RealEstatePropertyForm({
  saving = false,
  error,
  property,
  submitLabel = 'Add property',
  onSubmit,
  onCancel,
}: Props) {
  const [name, setName] = useState(property?.name ?? '');
  const [propertyType, setPropertyType] = useState<RealEstatePropertyType>(
    property?.propertyType ?? 'apartment'
  );
  const [address, setAddress] = useState(property?.address ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(property?.websiteUrl ?? '');
  const [currentValue, setCurrentValue] = useState(numberToInput(property?.currentValue));
  const [purchasePrice, setPurchasePrice] = useState(numberToInput(property?.purchasePrice));
  const [purchaseDate, setPurchaseDate] = useState(property?.purchaseDate ?? '');
  const [mortgageBalance, setMortgageBalance] = useState(numberToInput(property?.mortgageBalance));
  const [monthlyRent, setMonthlyRent] = useState(numberToInput(property?.monthlyRent));
  const [appreciationRate, setAppreciationRate] = useState(
    numberToInput(property?.appreciationRate)
  );
  const [notes, setNotes] = useState(property?.notes ?? '');
  const [validationError, setValidationError] = useState<string | null>(null);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setValidationError('Property name is required.');
      return;
    }

    const parsedValue = Number(currentValue);
    if (!currentValue.trim() || !Number.isFinite(parsedValue)) {
      setValidationError('Enter a valid market value.');
      return;
    }

    setValidationError(null);
    onSubmit({
      name: trimmedName,
      propertyType,
      address: address.trim() || undefined,
      websiteUrl: websiteUrl.trim() || undefined,
      currentValue: parsedValue,
      purchasePrice: parseOptionalNumber(purchasePrice),
      purchaseDate: purchaseDate.trim() || undefined,
      mortgageBalance: parseOptionalNumber(mortgageBalance),
      monthlyRent: parseOptionalNumber(monthlyRent),
      appreciationRate: parseOptionalNumber(appreciationRate),
      notes: notes.trim() || undefined,
    });
  }

  const displayError = validationError ?? error;

  return (
    <form className="sidebarFormStack" onSubmit={handleSubmit}>
      <GlobalInput
        label="Property name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="e.g. Berlin rental flat"
        autoComplete="off"
        required
      />

      <GlobalDropdown
        label="Type"
        value={propertyType}
        onChange={(value) => setPropertyType(value as RealEstatePropertyType)}
        options={REAL_ESTATE_TYPE_OPTIONS}
      />

      <GlobalInput
        label="Address (optional)"
        value={address}
        onChange={(event) => setAddress(event.target.value)}
        placeholder="e.g. Friedrichstr. 12, 10117 Berlin"
        autoComplete="off"
      />

      <GlobalInput
        label="Listing / management website (optional)"
        value={websiteUrl}
        onChange={(event) => setWebsiteUrl(event.target.value)}
        placeholder="e.g. immoscout24.de"
        autoComplete="off"
      />

      {websiteUrl.trim() ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Favicon websiteUrl={websiteUrl} size={24} alt="" className="companyFavicon" />
          <span className="muted" style={{ fontSize: '0.875rem' }}>
            Favicon preview
          </span>
        </div>
      ) : null}

      <GlobalInput
        label="Current market value (€)"
        type="number"
        inputMode="decimal"
        step="0.01"
        value={currentValue}
        onChange={(event) => setCurrentValue(event.target.value)}
        placeholder="0.00"
        required
      />

      <GlobalInput
        label="Outstanding mortgage (€) (optional)"
        type="number"
        inputMode="decimal"
        step="0.01"
        value={mortgageBalance}
        onChange={(event) => setMortgageBalance(event.target.value)}
        placeholder="0.00"
      />
      <p className="muted" style={{ margin: '-8px 0 0', fontSize: '0.8125rem' }}>
        Counts as a liability — net equity is value minus mortgage.
      </p>

      <GlobalInput
        label="Monthly rent (€) (optional)"
        type="number"
        inputMode="decimal"
        step="0.01"
        value={monthlyRent}
        onChange={(event) => setMonthlyRent(event.target.value)}
        placeholder="0.00"
      />

      <GlobalInput
        label="Purchase price (€) (optional)"
        type="number"
        inputMode="decimal"
        step="0.01"
        value={purchasePrice}
        onChange={(event) => setPurchasePrice(event.target.value)}
        placeholder="0.00"
      />

      <GlobalDatepicker
        label="Purchase date (optional)"
        value={purchaseDate}
        onChange={(event) => setPurchaseDate(event.target.value)}
      />

      <GlobalInput
        label="Appreciation rate % p.a. (optional)"
        type="number"
        inputMode="decimal"
        step="0.1"
        value={appreciationRate}
        onChange={(event) => setAppreciationRate(event.target.value)}
        placeholder="e.g. 2.5"
      />

      <GlobalTextarea
        label="Notes (optional)"
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
        placeholder="Tenant, condition, plans…"
      />

      {displayError ? <p className="sidebarFormError">{displayError}</p> : null}

      <div className="sidebarFormActions">
        <GlobalButton type="button" variant="secondary" disabled={saving} onClick={onCancel}>
          Cancel
        </GlobalButton>
        <GlobalButton type="submit" variant="primary" isLoading={saving} disabled={saving}>
          {submitLabel}
        </GlobalButton>
      </div>
    </form>
  );
}
